import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import { authApi, projectsApi } from '../services/api';

import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ShieldIcon from '@mui/icons-material/Shield';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', organization: '', password: '', role: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const passwordRef = useRef(null);
  const [error, setError] = useState('');
  const [allProjects, setAllProjects] = useState([]);
  const [userProjects, setUserProjects] = useState([]); // id[]

  const fetchUsers = async () => {
    try {
      const res = await authApi.getUsers();
      setUsers(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchUsers();
      // Получаем проекты с назначенными пользователями
      const res = await projectsApi.getAll();
      // Для каждого проекта получаем пользователей (если есть поле users)
      // Если нет, попробуем получить через projectsApi.getAll() с расширенным select на бэке
      setAllProjects(res.data);
    };
    fetchData();
  }, []);

  // Генерация случайного пароля
  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
    let newPassword = '';
    
    // Обеспечиваем наличие хотя бы одной цифры, одной буквы и одного специального символа
    newPassword += 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.charAt(Math.floor(Math.random() * 52));
    newPassword += '0123456789'.charAt(Math.floor(Math.random() * 10));
    newPassword += '!@#$%^&*()_+'.charAt(Math.floor(Math.random() * 12));
    
    // Добавляем оставшиеся символы
    for (let i = 3; i < length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    // Перемешиваем пароль
    newPassword = newPassword.split('').sort(() => 0.5 - Math.random()).join('');
    
    setForm({ ...form, password: newPassword });
    // Показываем пароль после генерации
    setShowPassword(true);
  };
  
  // Копирование пароля в буфер обмена
  const copyToClipboard = () => {
    if (form.password) {
      navigator.clipboard.writeText(form.password)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch(err => {
          console.error('Ошибка при копировании: ', err);
        });
    }
  };

  const handleSubmit = async () => {
    try {
      let userId = form.id;
      // Если поле organization пустое — явно отправляем null
      const submitForm = { ...form };
      if (!submitForm.organization || submitForm.organization.trim() === '') {
        submitForm.organization = null;
      }
      if (form.id) {
        await authApi.updateUser(form.id, submitForm);
      } else {
        const res = await authApi.register(submitForm);
        userId = res.data.id;
      }
      // Сначала удалить все проекты пользователя (если редактирование)
      if (form.id) {
        // Получить проекты, где назначен этот пользователь
        const userIdNum = Number(userId);
        const assigned = allProjects
          .filter(p => Array.isArray(p.users) && p.users.some(up => up.userId === userIdNum))
          .map(p => p.id);
        // Удалить все проекты, которых нет в userProjects
        for (const pid of assigned) {
          if (!userProjects.includes(pid)) {
            await projectsApi.removeUser(userId, pid);
          }
        }
      }
      // Назначить выбранные проекты, если пользователь ещё не назначен
      for (const pid of userProjects) {
        const project = allProjects.find(p => p.id === pid);
        if (!project || !Array.isArray(project.users) || !project.users.some(up => up.userId === userId)) {
          await projectsApi.assignUser(userId, pid);
        }
      }
      setOpen(false);
      setForm({ email: '', name: '', organization: '', password: '', role: '' });
      setUserProjects([]);
      await fetchUsers();
      // Обновить список проектов с пользователями
      const res = await projectsApi.getAll();
      setAllProjects(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleEditOpen = (u) => {
    setForm({ ...u, password: '' });
    // Получить id проектов, где назначен этот пользователь
    const userProjIds = allProjects.filter(p => Array.isArray(p.users) && p.users.some(up => up.userId === u.id)).map(p => p.id);
    setUserProjects(userProjIds);
    setOpen(true);
    setError('');
  };


  const handleDelete = async (id) => {
    if (window.confirm('Удалить пользователя?')) {
      await authApi.deleteUser(id);
      await fetchUsers();
      // Обновить список проектов с пользователями
      const res = await projectsApi.getAll();
      setAllProjects(res.data);
    }
  };


  return (
    <Container sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Пользователи</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>Создать пользователя</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Имя</TableCell>
              <TableCell>Организация</TableCell>
              <TableCell align="center">Статус</TableCell>
              <TableCell>Проекты</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => {
              // Получаем список проектов пользователя
              const userProjectsList = allProjects.filter(p => Array.isArray(p.users) && p.users.some(up => up.userId === u.id));
              return (
                <TableRow key={u.id} hover>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.organization}</TableCell>
                  <TableCell align="center">
                    {u.role === 'admin' && <ShieldIcon sx={{ color: '#1976d2' }} titleAccess="Администратор" />}
                    {u.role === 'manager' && <SupervisorAccountIcon sx={{ color: '#388e3c' }} titleAccess="Менеджер" />}
                    {u.role === 'user' && <PersonIcon sx={{ color: '#757575' }} titleAccess="Пользователь" />}
                    {u.role === 'agent' && <SupportAgentIcon sx={{ color: '#ff9800' }} titleAccess="Агент" />}
                  </TableCell>
                  <TableCell>
                    {userProjectsList.length
                      ? userProjectsList.map(p => p.name).join(', ')
                      : <span style={{ color: '#aaa' }}>Нет проектов</span>}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditOpen(u)} title="Редактировать пользователя">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(u.id)} title="Удалить пользователя">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{form.id ? 'Редактировать пользователя' : 'Создать пользователя'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField fullWidth label="Email" margin="dense" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!!form.id} />
          <TextField fullWidth label="Имя" margin="dense" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <TextField fullWidth label="Организация" margin="dense" value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} />
          <TextField 
            fullWidth 
            label="Пароль" 
            type={showPassword ? "text" : "password"} 
            margin="dense" 
            value={form.password || ''} 
            onChange={e => setForm({ ...form, password: e.target.value })}
            ref={passwordRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {/* Кнопка генерации пароля */}
                  <Tooltip title="Сгенерировать пароль">
                    <IconButton
                      onClick={generatePassword}
                      edge="end"
                      size="small"
                    >
                      <AutoFixHighIcon />
                    </IconButton>
                  </Tooltip>
                  
                  {/* Кнопка видимости пароля */}
                  <Tooltip title={showPassword ? "Скрыть пароль" : "Показать пароль"}>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Tooltip>
                  
                  {/* Кнопка копирования пароля */}
                  {form.password && (
                    <Tooltip title={copySuccess ? "Скопировано!" : "Копировать пароль"}>
                      <IconButton
                        onClick={copyToClipboard}
                        edge="end"
                        size="small"
                        color={copySuccess ? "success" : "default"}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="role-label">Роль</InputLabel>
            <Select
              labelId="role-label"
              value={form.role || ''}
              onChange={e => setForm({ ...form, role: e.target.value })}
              label="Роль"
            >
              <MenuItem value="user">Пользователь</MenuItem>
              <MenuItem value="admin">Администратор</MenuItem>
              <MenuItem value="manager">Менеджер</MenuItem>
              <MenuItem value="agent">Агент</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel id="projects-label">Проекты</InputLabel>
            <Select
              labelId="projects-label"
              multiple
              value={userProjects}
              onChange={e => setUserProjects(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label="Проекты" />}
              renderValue={selected => allProjects.filter(p => selected.includes(p.id)).map(p => p.name).join(', ')}
            >
              {allProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  <Checkbox checked={userProjects.indexOf(project.id) > -1} />
                  <ListItemText primary={project.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSubmit}>{form.id ? 'Сохранить' : 'Создать'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersPage;
